import React from 'react'
import { IconStarFilled, IconCheck, IconInfoCircle } from '@tabler/icons-react'
import { isEmpty, isNil } from 'lodash'
import classNames from 'classnames'

import { Checkbox } from '../forms/Checkbox'
import { Button } from '../buttons/Button'
import { InteractiveTooltip } from '../tooltips/InteractiveTooltip'

export const SearchableDropdownItem = ({
  option,
  isMultiSelect,
  handleSelect,
  selected,
  pinnedAccessor,
}) => {
  const isSelected = isMultiSelect
    ? !isEmpty(selected.filter((v) => v.value === option.value))
    : option.value === selected || Boolean(option.selected)

  const handleSelectOption = () => {
    if (option.disabled) {
      return
    }

    if (isMultiSelect) {
      const updatedValue = isSelected
        ? selected.filter((v) => v.value !== option.value)
        : [...selected, option]
      handleSelect(option.value, updatedValue)
    } else {
      handleSelect(option.value)
    }
  }

  return (
    <div
      role="option"
      aria-selected={isSelected}
      aria-disabled={Boolean(option.disabled)}
      tabIndex={-1}
      className={classNames('searchable-dropdown-result', {
        active: isSelected,
        disabled: option.disabled,
      })}
      data-testid="searchable-dropdown-result"
      onClick={handleSelectOption}
      onMouseDown={(event) => {
        if (option.disabled) {
          event.preventDefault()
        }
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          handleSelectOption()
        }
      }}
    >
      <div
        className={classNames('searchable-dropdown-result-content', {
          'with-description': Boolean(option.description),
        })}
      >
        {option[pinnedAccessor] && <IconStarFilled className="icon-svg status-warning" />}
        {/* Decoration: the row owns click and keydown. Selection state is not exposed to
            assistive tech here yet — that needs a real listbox/option treatment on the row. */}
        {isMultiSelect && (
          <Checkbox
            checked={isSelected}
            readOnly
            aria-hidden="true"
            tabIndex={-1}
            data-testid={`checkbox-${option.value}-${isSelected ? 'checked' : 'not-checked'}`}
          />
        )}
        {option.icon}
        <div>
          {/* labelNode carries rich row content (chips, multi-line detail) while
              `label` stays the plain string the trigger and search rely on. */}
          <div>{option.labelNode || option.label}</div>
          {option.description && (
            <div className="searchable-dropdown-result-description">{option.description}</div>
          )}
        </div>
      </div>

      {!isNil(option.meta) && (
        <span className="searchable-dropdown-result-meta">{option.meta}</span>
      )}

      {!isMultiSelect && isSelected && (
        <IconCheck
          className="searchable-dropdown-result-check icon-svg status-brand"
          data-testid={`selected-item-${option.value}`}
        />
      )}

      {!isEmpty(option.actions) && (
        <div className="searchable-dropdown-item-actions">
          {option.actions.map((action) => (
            <Button
              key={action.label}
              xs
              basic
              square
              onClick={(event) => {
                event.stopPropagation()
                action.onClick(event)
              }}
            >
              {action.icon}
            </Button>
          ))}
        </div>
      )}

      {option.infoTooltipDetails && (
        <InteractiveTooltip content={option.infoTooltipDetails} placement="top">
          <IconInfoCircle className="icon-svg muted-text" />
        </InteractiveTooltip>
      )}
    </div>
  )
}
